import { useCallback } from 'react'
import { RestrictedCheckArgs, RestrictedCheckStatus } from '@typesApp/Customer'
import { useLazyRestrictedCheckQuery } from '@redux/queries/claimRecords'
import { formatCommerceCode } from '@utils/formatCommerceCode'
import { useEnvironment } from './useEnvironment'
import { useSessionInfo } from './useSessionInfo'

type UseRestrictedCheckReturn = {
  isLoading: boolean
  fetchRestrictedStatus: (customerNumber?: string) => Promise<RestrictedCheckStatus>
}

export const useRestrictedCheck = (): UseRestrictedCheckReturn => {
  const { customer } = useSessionInfo()
  const { ecommerceCode, ecommerceTransactionHost } = useEnvironment()
  const [triggerFetch, { isLoading, isFetching }] = useLazyRestrictedCheckQuery()

  const fetchRestrictedStatus = useCallback(
    async (customerNumber?: string): Promise<RestrictedCheckStatus> => {
      const currentCustomerNumber = customerNumber ?? customer?.id
      if (!currentCustomerNumber) {
        throw new Error('Customer number required for restricted check')
      }

      const args: RestrictedCheckArgs = {
        customerNumber: currentCustomerNumber,
        ecommerce: formatCommerceCode({ ecommerceCode, ecommerceTransactionHost }),
      }

      return await triggerFetch(args).unwrap()
    },
    [triggerFetch, customer, ecommerceCode, ecommerceTransactionHost]
  )

  return {
    isLoading: isLoading || isFetching,
    fetchRestrictedStatus,
  }
}
